// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Age Verification Page (18+ MANDATORY)
// AGLC/AGCO Regulatory Requirement
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AgeVerifyPage({ onVerified }) {
  const navigate = useNavigate();
  const [birthDate, setBirthDate] = useState({ year: '', month: '', day: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [responsibleAccepted, setResponsibleAccepted] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = () => {
    setError('');
    const { year, month, day } = birthDate;
    if (!year || !month || !day) {
      setError('Please enter your full date of birth');
      return;
    }

    const dob = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    if (age < 18) {
      setError('You must be 18 or older to access Forge Casino');
      return;
    }
    if (isNaN(dob.getTime()) || parseInt(year) < 1900 || parseInt(year) > today.getFullYear()) {
      setError('Please enter a valid date of birth');
      return;
    }
    if (!termsAccepted) {
      setError('You must accept the Terms of Service');
      return;
    }
    if (!responsibleAccepted) {
      setError('You must acknowledge the Responsible Gaming Policy');
      return;
    }

    // Store verification
    localStorage.setItem('forgecasino_age_verified', JSON.stringify({
      verified: true,
      verifiedAt: new Date().toISOString(),
      dob: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
    }));

    if (onVerified) {
      onVerified();
    }
    // Only navigate if on the standalone /age-verify route
    // When inside AgeGate wrapper, onVerified sets state and re-renders — no navigation needed
    if (window.location.pathname === '/age-verify') {
      navigate('/login');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.icon}>{'🔥'}</span>
          <h1 style={styles.title}>FORGE CASINO</h1>
          <p style={styles.subtitle}>PROVABLY FAIR GAMING</p>
        </div>

        {/* Age Gate */}
        <div style={styles.ageGate}>
          <div style={styles.shield}>{'🛡️'}</div>
          <h2 style={styles.gateTitle}>AGE VERIFICATION REQUIRED</h2>
          <p style={styles.gateText}>
            You must be 18 years or older to access Forge Casino.
            This verification is required by the Alberta Gaming, Liquor & Cannabis Commission (AGLC).
          </p>

          {/* Date of Birth */}
          <div style={styles.dobSection}>
            <label style={styles.label}>DATE OF BIRTH</label>
            <div style={styles.dobInputs}>
              <select
                style={styles.select}
                value={birthDate.month}
                onChange={e => setBirthDate(prev => ({ ...prev, month: e.target.value }))}
              >
                <option value="">Month</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={String(i + 1)}>{new Date(0, i).toLocaleString('en', { month: 'long' })}</option>
                ))}
              </select>
              <select
                style={styles.select}
                value={birthDate.day}
                onChange={e => setBirthDate(prev => ({ ...prev, day: e.target.value }))}
              >
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, i) => (
                  <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                ))}
              </select>
              <select
                style={styles.select}
                value={birthDate.year}
                onChange={e => setBirthDate(prev => ({ ...prev, year: e.target.value }))}
              >
                <option value="">Year</option>
                {Array.from({ length: 80 }, (_, i) => {
                  const year = new Date().getFullYear() - 18 - i;
                  return <option key={year} value={String(year)}>{year}</option>;
                })}
              </select>
            </div>
          </div>

          {/* Checkboxes */}
          <div style={styles.checkboxSection}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                style={styles.checkbox}
              />
              I accept the Terms of Service and confirm I am legally permitted to gamble in my jurisdiction
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={responsibleAccepted}
                onChange={e => setResponsibleAccepted(e.target.checked)}
                style={styles.checkbox}
              />
              I acknowledge the Responsible Gaming Policy and understand I can set deposit limits, loss limits, and self-exclude at any time
            </label>
          </div>

          {/* Error */}
          {error && <div style={styles.error}>{error}</div>}

          {/* Verify Button */}
          <button style={styles.verifyBtn} onClick={handleVerify}>
            VERIFY & ENTER
          </button>

          {/* Help */}
          <div style={styles.helpSection}>
            <p style={styles.helpText}>
              Gambling should be fun. If you or someone you know has a gambling problem, call the Alberta Problem Gambling Helpline: <strong>1-866-332-2322</strong>
            </p>
            <div style={styles.badges}>
              <span style={styles.badge}>18+</span>
              <span style={styles.badge}>AGLC Compliant</span>
              <span style={styles.badge}>Provably Fair</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #0a0000 0%, #1a0000 50%, #0a0000 100%)',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    background: '#111',
    borderRadius: 16,
    border: '2px solid #DC2626',
    boxShadow: '0 0 40px #DC262622, 0 20px 60px rgba(0,0,0,0.5)',
    overflow: 'hidden',
  },
  header: {
    textAlign: 'center',
    padding: '24px 20px 16px',
    background: 'linear-gradient(180deg, #1a0000 0%, #111 100%)',
    borderBottom: '1px solid #DC262633',
  },
  icon: { fontSize: 40 },
  title: {
    fontSize: 24,
    fontWeight: 900,
    color: '#DC2626',
    letterSpacing: 4,
    margin: '8px 0 4px',
    fontFamily: "'Courier New', monospace",
  },
  subtitle: {
    fontSize: 10,
    color: '#00FF88',
    letterSpacing: 3,
    fontWeight: 700,
  },
  ageGate: {
    padding: '24px 20px',
  },
  shield: { fontSize: 48, textAlign: 'center' },
  gateTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: '#fff',
    textAlign: 'center',
    letterSpacing: 2,
    margin: '12px 0',
  },
  gateText: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    lineHeight: 1.6,
    marginBottom: 20,
  },
  dobSection: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 10,
    color: '#DC2626',
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 8,
  },
  dobInputs: {
    display: 'flex',
    gap: 8,
  },
  select: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: 8,
    border: '1px solid #333',
    background: '#0a0a0a',
    color: '#fff',
    fontSize: 13,
  },
  checkboxSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 20,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 11,
    color: '#aaa',
    lineHeight: 1.5,
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: 2,
    accentColor: '#DC2626',
  },
  error: {
    padding: '10px 12px',
    background: '#DC262622',
    border: '1px solid #DC262666',
    borderRadius: 8,
    color: '#DC2626',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 16,
  },
  verifyBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: 10,
    border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff',
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: 3,
    cursor: 'pointer',
    boxShadow: '0 0 20px #DC262644',
    marginBottom: 20,
  },
  helpSection: {
    textAlign: 'center',
    borderTop: '1px solid #222',
    paddingTop: 16,
  },
  helpText: {
    fontSize: 10,
    color: '#666',
    lineHeight: 1.6,
    marginBottom: 12,
  },
  badges: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 4,
    background: '#1a1a1a',
    border: '1px solid #333',
    fontSize: 9,
    color: '#888',
    fontWeight: 700,
    letterSpacing: 1,
  },
};
